import { useRef, useState } from 'react';

import type { Carousel } from '@smartapply/shared';

import {
  exportCarouselAsImagesZip,
  exportCarouselAsPdf,
} from '../../lib/carousel-export.js';
import { CarouselSlide } from './CarouselSlide.js';

interface CarouselPreviewProps {
  carousel: Carousel;
  onClose: () => void;
}

/**
 * Modal/overlay com:
 *   - Preview dos slides em strip horizontal (escala reduzida pra caber)
 *   - Botão "Exportar PDF" → gera PDF nativo via html2canvas + jsPDF
 *   - Botão fechar
 *
 * Os slides em tamanho REAL (1080×1350) ficam renderizados off-screen pra
 * serem capturados pelo html2canvas. O preview na tela é só visual reduzido.
 */
export function CarouselPreview({ carousel, onClose }: CarouselPreviewProps) {
  const [exporting, setExporting] = useState<null | 'zip' | 'pdf'>(null);
  const [error, setError] = useState<string | null>(null);
  const exportRefs = useRef<(HTMLDivElement | null)[]>([]);

  function getElements(): HTMLDivElement[] {
    return exportRefs.current.filter((el): el is HTMLDivElement => el !== null);
  }

  async function handleExportZip() {
    setExporting('zip');
    setError(null);
    try {
      const filename = `carrossel-imagens-${new Date().toISOString().slice(0, 10)}.zip`;
      await exportCarouselAsImagesZip(getElements(), filename);
    } catch (e) {
      setError(
        e instanceof Error
          ? `Falha ao exportar ZIP: ${e.message}`
          : 'Falha desconhecida ao exportar ZIP.',
      );
    } finally {
      setExporting(null);
    }
  }

  async function handleExportPdf() {
    setExporting('pdf');
    setError(null);
    try {
      const filename = `carrossel-${new Date().toISOString().slice(0, 10)}.pdf`;
      await exportCarouselAsPdf(getElements(), filename);
    } catch (e) {
      setError(
        e instanceof Error
          ? `Falha ao exportar PDF: ${e.message}`
          : 'Falha desconhecida ao exportar PDF.',
      );
    } finally {
      setExporting(null);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="carousel-preview-title"
      className="fixed inset-0 z-50 flex items-stretch overflow-y-auto bg-[var(--ink-primary)]/85 backdrop-blur-sm"
    >
      <div className="m-auto w-full max-w-6xl px-4 py-12">
        <header className="mb-8 flex items-center justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--bg-paper)]/70">
              Carrossel pra LinkedIn
            </p>
            <h2
              id="carousel-preview-title"
              className="mt-1 font-serif text-3xl font-medium leading-tight text-[var(--bg-paper)]"
            >
              {carousel.slides.length} slides prontos
              <span className="text-[var(--accent-saffron)]">.</span>
            </h2>
            <p className="mt-2 font-sans text-sm text-[var(--bg-paper)]/70">
              Revise os slides abaixo e exporte como PDF pra subir no LinkedIn como{' '}
              <em>document post</em>.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => void handleExportZip()}
              disabled={exporting !== null}
              title="Baixa um ZIP com as imagens PNG individuais (1080×1350 cada). Sobe no LinkedIn como carrossel de imagens swipável nativo (até 9 imagens por post)."
              className="rounded-sm bg-[var(--accent-saffron)] px-5 py-3 font-sans text-sm font-medium text-[var(--ink-primary)] transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {exporting === 'zip' ? 'Gerando ZIP...' : 'Baixar imagens (ZIP)'}
            </button>
            <button
              type="button"
              onClick={() => void handleExportPdf()}
              disabled={exporting !== null}
              title="Baixa em PDF único. Sobe no LinkedIn como document post — visual diferente do carrossel de imagens."
              className="rounded-sm border border-[var(--bg-paper)]/30 px-4 py-3 font-sans text-sm text-[var(--bg-paper)] hover:bg-[var(--bg-paper)]/10 disabled:opacity-50"
            >
              {exporting === 'pdf' ? 'Gerando PDF...' : 'Baixar PDF (documento)'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-sm border border-[var(--bg-paper)]/30 px-4 py-3 font-sans text-sm text-[var(--bg-paper)] hover:bg-[var(--bg-paper)]/10"
            >
              Fechar
            </button>
          </div>
        </header>

        {error && (
          <p
            role="alert"
            className="mb-4 rounded-sm border border-[var(--accent-crimson)]/40 bg-[var(--accent-crimson)]/10 px-4 py-3 font-sans text-sm text-[var(--bg-paper)]"
          >
            {error}
          </p>
        )}

        <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--bg-paper)]/50">
          preview (proporção real do LinkedIn: 4:5 vertical)
        </p>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {carousel.slides.map((slide) => (
            <div
              key={slide.index}
              className="overflow-hidden rounded-sm shadow-2xl ring-1 ring-[var(--bg-paper)]/20"
            >
              <CarouselSlide slide={slide} totalSlides={carousel.slides.length} />
            </div>
          ))}
        </div>

        <div className="mt-8 space-y-2 font-sans text-xs italic leading-relaxed text-[var(--bg-paper)]/60">
          <p>
            <strong className="not-italic text-[var(--bg-paper)]/80">Carrossel de imagens (recomendado):</strong>{' '}
            baixe o ZIP, extraia, "Iniciar publicação" → ícone de imagem → selecione todas as PNGs em
            ordem (slide-01 → slide-08). LinkedIn renderiza como carrossel swipável nativo.
          </p>
          <p>
            <strong className="not-italic text-[var(--bg-paper)]/80">Documento PDF:</strong>{' '}
            baixe o PDF, "Iniciar publicação" → ícone "+" → "Adicionar documento" → seleciona o PDF.
            Aparece como document post (visual diferente do carrossel de imagens).
          </p>
        </div>
      </div>

      {/* Slides em tamanho REAL (1080×1350) renderizados off-screen pra
          html2canvas capturar. Fixed position + opacity 0 não atrapalha
          o layout visível, mas mantém o DOM real disponível. */}
      <div
        aria-hidden
        style={{
          position: 'fixed',
          top: '0',
          left: '-99999px',
          opacity: 0,
          pointerEvents: 'none',
        }}
      >
        {carousel.slides.map((slide, idx) => (
          <div
            key={`export-${slide.index}`}
            ref={(el) => {
              exportRefs.current[idx] = el;
            }}
          >
            <CarouselSlide slide={slide} totalSlides={carousel.slides.length} exportMode />
          </div>
        ))}
      </div>
    </div>
  );
}
